import { filterAllDocsResult } from './db-service-helper';
// RUNS IN BROWSER AND NODEJS!
import PouchDB from 'pouchdb';
import pouchAdapterMemory from 'pouchdb-adapter-memory';
import pouchFind from 'pouchdb-find';
PouchDB.plugin(pouchAdapterMemory);
PouchDB.plugin(pouchFind);
export class DbService {
    dbFields;
    dbFieldContexts;
    dbFieldContextOptions;
    storageMock;
    constructor() {
        const randomized = Math.random();
        this.dbFields = new PouchDB(randomized + 'fields', { adapter: 'memory' });
        this.dbFieldContexts = new PouchDB(randomized + 'fieldcontexts', { adapter: 'memory' });
        this.dbFieldContextOptions = new PouchDB(randomized + 'fieldcontextoptions', { adapter: 'memory' });
        this.storageMock = new PouchDB(randomized + 'storageMock', { adapter: 'memory' });
    }
    async createCustomfield(field, contexts) {
        field['_id'] = field.id;
        delete field.id;
        await this.dbFields.put(field);
        for (const context of contexts) {
            await this.createCustomfieldContext(field['_id'], context);
        }
    }
    async createCustomfieldContext(fieldId, context) {
        context['fkFieldId'] = fieldId;
        context['_id'] = context.id;
        delete context.id;
        await this.dbFieldContexts.put(context);
    }
    // FIXME: limit https://pouchdb.com/2014/04/14/pagination-strategies-with-pouchdb.html
    async getAllCustomfields() {
        const fields = await this.dbFields.allDocs({ include_docs: true });
        //console.log('db', fields);
        return filterAllDocsResult(fields);
    }
    async _getAllContexts() {
        const contexts = await this.dbFieldContexts.allDocs({ include_docs: true });
        return filterAllDocsResult(contexts);
    }
    async getAllCustomfieldContexts(fieldId) {
        const contextsDb = await this._getAllContexts();
        const contexts = contextsDb.filter((c) => {
            return c['fkFieldId'] === fieldId;
        });
        return {
            maxResults: 100,
            startAt: 0,
            total: contexts.length,
            isLast: true,
            values: contexts,
        };
    }
    async createCustomfieldContextOption(fieldId, contextId, optionId, optionValue, disabled, parentOptionId) {
        const option = { fkFieldId: fieldId, fkContextId: contextId, _id: `${optionId}`, value: optionValue, disabled: disabled, optionId: parentOptionId ? parentOptionId : null };
        await this.dbFieldContextOptions.put(option);
    }
    async updateCustomfieldContextOption(_fieldId, _contextId, updateObj) {
        try {
            const option = await this.dbFieldContextOptions.get(`${updateObj.id}`);
            option.id = updateObj.id;
            option.value = updateObj.value;
            option.disabled = updateObj.disabled;
            const res = await this.dbFieldContextOptions.put(option);
            return res.ok;
        }
        catch (err) {
            console.log('DB-SERVICE CATCH ERROR:', JSON.stringify(err, null, 2));
            return false;
        }
    }
    async deleteCustomfieldContextOption(_fieldId, _contextId, deleteId) {
        try {
            const option = await this.dbFieldContextOptions.get(`${deleteId}`);
            const res = await this.dbFieldContextOptions.remove(option);
            return res.ok;
        }
        catch (err) {
            console.log('DB-SERVICE CATCH ERROR:', JSON.stringify(err, null, 2));
            return false;
        }
    }
    async moveCustomfieldContextOption(_fieldId, _contextId, optionIdToMove, afterOptionId, position) {
        // not implemented
    }
    async createCustomfieldContextOptionsBulk(fieldId, contextId, options) {
        const optionsToCreate = [];
        for (const option of options) {
            optionsToCreate.push({ fkFieldId: fieldId, fkContextId: contextId, _id: `${option.id}`, value: option.value, disabled: option.disabled, optionId: option.optionId ? option.optionId : null });
        }
        await this.dbFieldContextOptions.bulkDocs(optionsToCreate);
    }
    async getAllCustomfieldOptions() {
        const optionsDb = await this.dbFieldContextOptions.allDocs({ include_docs: true });
        return filterAllDocsResult(optionsDb);
    }
    async getAllCustomfieldContextOptions(fieldId, contextId, startAt, maxResults, parentOptionId) {
        const optionsDb = await this.getAllCustomfieldOptions();
        let filtertedOptions = [];
        if (parentOptionId) {
            const parentOptionAsArray = optionsDb.filter((c) => {
                return c['fkFieldId'] === fieldId && `${c['fkContextId']}` === `${contextId}` && `${c['id']}` === `${parentOptionId}`;
            });
            const childOptionsAsArray = optionsDb
                .filter((c) => {
                return c['fkFieldId'] === fieldId && `${c['fkContextId']}` === `${contextId}` && `${c['optionId']}` === `${parentOptionId}`;
            })
                .sort((s1, s2) => (parseInt(`${s1.id}`, 10) < parseInt(`${s2.id}`, 10) ? -1 : 1));
            filtertedOptions = [...parentOptionAsArray, ...childOptionsAsArray];
            // console.log('filtertedOptions', filtertedOptions);
        }
        else {
            filtertedOptions = optionsDb
                .filter((c) => {
                return c['fkFieldId'] === fieldId && `${c['fkContextId']}` === `${contextId}`;
            })
                .sort((s1, s2) => (parseInt(`${s1.id}`, 10) < parseInt(`${s2.id}`, 10) ? -1 : 1));
        }
        const total = filtertedOptions.length;
        const maxResultsSafe = total < startAt + maxResults ? total : maxResults;
        const optionsSliced = filtertedOptions.slice(startAt, maxResultsSafe);
        const optionsTransformedToFitApi = optionsSliced.map((o) => ({
            disabled: o.disabled,
            id: o.id,
            optionId: o.optionId,
            value: o.value,
        }));
        return {
            startAt: startAt,
            maxResults: maxResultsSafe,
            total: total,
            isLast: total < startAt + maxResults,
            values: optionsTransformedToFitApi,
        };
    }
    //
    // STORAGE API
    //
    async storageMockGet(key) {
        try {
            const res = await this.storageMock.get(key, { latest: true });
            if (!res._deleted) {
                return res.values;
            }
            return [];
        }
        catch (err) {
            // console.log('storageMockGet - err: ', err);
            return [];
        }
    }
    async storageMockDelete(key) {
        try {
            await this.storageMock.remove(key);
        }
        catch (err) {
            // console.log('storageMockDelete - err: ', err);
        }
    }
    async storageMockSet(key, payload) {
        try {
            try {
                const res = await this.storageMock.get(key, { latest: true });
                // console.log('storageMockSet UPDATE'); // POUCHDB
                await this.storageMock.put({ _id: key, _rev: res._rev, values: payload });
            }
            catch (err) {
                // console.log('storageMockSet INSERT'); // POUCHDB
                await this.storageMock.put({ _id: key, values: payload });
            }
        }
        catch (err) {
            console.log('storageMockSet - err: ', err);
        }
        //console.log('POUCH', JSON.stringify(await this.storageMock.allDocs(), null, 2));
    }
}
